// src/components/sessions/SessionList.jsx
import React, { useState, useCallback, useEffect, useRef, useMemo, memo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeftIcon,
  UserGroupIcon,
  UserIcon,
  PlusIcon,
  VideoCameraIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import {
  getCourseSessions,
  getCourseTeachers,
  createSession,
  deleteSession,
  getCourseStudents,
  enrollStudentsInCourse,
  unenrollStudentsFromCourse,
  getAvailableStudentsForCourse,
  getAvailableTeachersForCourse,
  addCourseTeacher,
  removeCourseTeacher,
  getCourse
} from '@/services/api';
import SessionItem from './SessionItem';
import SessionChatModal from './SessionChatModal';
import { Pagination } from '../dashboard/MyCourses';
import { useWebSocket } from '@/hooks/useWebSocket';
import { toast } from 'sonner';

// Import Modal Components
import AddSessionModal from './modals/AddSessionModal';
import ManageStudentsModal from './modals/ManageStudentsModal';
import ManageTeachersModal from './modals/ManageTeachersModal';

// Helper function to safely parse dates
const safeParseDate = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
};

// Timer hook for real-time updates
const useTimer = () => {
  const [, forceUpdate] = useState({});
  
  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate({});
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);
};

// Recording Consent Modal Component
const RecordingConsentModal = memo(({ session, onConsent, onCancel }) => {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <VideoCameraIcon className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="ml-3 text-lg font-semibold text-slate-900">
            {t('sessions.recording.modalTitle')}
          </h3>
        </div>
        
        <div className="mb-6">
          <p className="text-sm text-slate-600 mb-3">
            {t('sessions.recording.modalDescription', { 
              title: session?.title || t('sessions.session') 
            })}
          </p>
          <ul className="text-sm text-slate-700 space-y-1 ml-4">
            <li className="flex items-start">
              <span className="text-slate-400 mr-2">•</span>
              <span>{t('sessions.recording.availableTo.instructors')}</span>
            </li>
            <li className="flex items-start">
              <span className="text-slate-400 mr-2">•</span>
              <span>{t('sessions.recording.availableTo.participants')}</span>
            </li>
          </ul>
          <p className="text-xs text-slate-500 mt-3">
            {t('sessions.recording.disclaimer')}
          </p>
        </div>

        <div className="mb-6">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="mt-1 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
            />
            <span className="ml-2 text-sm text-slate-700">
              {t('sessions.recording.consentCheckbox')}
            </span>
          </label>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConsent}
            disabled={!isChecked}
            className={`flex-1 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
              isChecked
                ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {t('sessions.recording.acceptAndJoin')}
          </button>
        </div>
      </div>
    </div>
  );
});

RecordingConsentModal.displayName = 'RecordingConsentModal';

// Memoized Session List Content Component
const SessionListContent = memo(({ 
  sessions, 
  activeTab, 
  currentPage,
  itemsPerPage,
  userRole,
  isOwner,
  sessionStatuses,
  sessionDataMap,
  onJoinSession,
  onDeleteSession,
  onViewChat,
  onAddSession,
  onPageChange
}) => {
  const { t } = useTranslation();
  const currentSessions = activeTab === 'upcoming' ? sessions.upcoming : sessions.completed;
  const totalPages = Math.ceil(currentSessions.length / itemsPerPage);
  const visibleSessions = currentSessions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (visibleSessions.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12 bg-slate-50 rounded-xl text-slate-500 text-sm sm:text-base">
        <VideoCameraIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <p>{t(`sessions.empty.${activeTab}`)}</p>
        {userRole === 'teacher' && activeTab === 'upcoming' && (
          <button
            onClick={onAddSession}
            className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
          >
            {t('sessions.scheduleSession')}
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {visibleSessions.map((session) => (
          <SessionItem
            key={session.id}
            session={session}
            sessionStatus={sessionStatuses[session.id]}
            sessionData={sessionDataMap[session.id]}
            isUpcoming={activeTab === 'upcoming'}
            userRole={userRole}
            isOwner={isOwner}
            onJoinSession={onJoinSession}
            onDeleteSession={onDeleteSession}
            onViewChat={() => onViewChat(session)}
          />
        ))}
      </div>
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
}, (prevProps, nextProps) => {
  const areSessionsEqual = (prev, next) => {
    if (prev.length !== next.length) return false;
    return prev.every((session, index) => {
      const nextSession = next[index];
      return session.id === nextSession.id;
    });
  };
  
  const prevSessionIds = prevProps.activeTab === 'upcoming' 
    ? prevProps.sessions.upcoming.map(s => `${s.id}-${prevProps.sessionStatuses[s.id]}-${prevProps.sessionDataMap[s.id]?.chat_log_available}`).join(',')
    : prevProps.sessions.completed.map(s => `${s.id}-${prevProps.sessionStatuses[s.id]}-${prevProps.sessionDataMap[s.id]?.chat_log_available}`).join(',');
    
  const nextSessionIds = nextProps.activeTab === 'upcoming'
    ? nextProps.sessions.upcoming.map(s => `${s.id}-${nextProps.sessionStatuses[s.id]}-${nextProps.sessionDataMap[s.id]?.chat_log_available}`).join(',')
    : nextProps.sessions.completed.map(s => `${s.id}-${nextProps.sessionStatuses[s.id]}-${nextProps.sessionDataMap[s.id]?.chat_log_available}`).join(',');
  
  return (
    prevProps.activeTab === nextProps.activeTab &&
    prevProps.currentPage === nextProps.currentPage &&
    prevProps.itemsPerPage === nextProps.itemsPerPage &&
    prevProps.userRole === nextProps.userRole &&
    prevProps.isOwner === nextProps.isOwner &&
    prevSessionIds === nextSessionIds &&
    areSessionsEqual(prevProps.sessions.upcoming, nextProps.sessions.upcoming) &&
    areSessionsEqual(prevProps.sessions.completed, nextProps.sessions.completed) &&
    prevProps.onJoinSession === nextProps.onJoinSession &&
    prevProps.onDeleteSession === nextProps.onDeleteSession &&
    prevProps.onViewChat === nextProps.onViewChat &&
    prevProps.onAddSession === nextProps.onAddSession &&
    prevProps.onPageChange === nextProps.onPageChange
  );
});

SessionListContent.displayName = 'SessionListContent';

const SessionList = ({ 
  user, 
  userRole,
  initialTab,
  onCourseNameLoad,
  institutionName
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { courseId } = useParams();
  
  useTimer();
  
  const { sendMessage: wsSendMessage, addMessageHandler, isConnected } = useWebSocket();
  
  const [sessions, setSessions] = useState({ completed: [], upcoming: [] });
  const [sessionStatuses, setSessionStatuses] = useState({});
  const [sessionDataMap, setSessionDataMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab || 'upcoming');
  const [currentPage, setCurrentPage] = useState(1);
  const [courseName, setCourseName] = useState(location.state?.courseName || '');
  const itemsPerPage = 5;

  const [recordingConsentSession, setRecordingConsentSession] = useState(null);

  const hourDropdownRef = useRef(null);
  const minuteDropdownRef = useRef(null);
  const sessionsRef = useRef({ completed: [], upcoming: [] });
  const callbacksRef = useRef({});
  const stateRef = useRef({});
  const tRef = useRef(t);
  
  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const [modalStates, setModalStates] = useState({
    showAddSessionModal: false,
    showStudentsModal: false,
    showTeachersModal: false,
    showChatModal: false,
    selectedSessionForChat: null
  });

  const [newSessionTitle, setNewSessionTitle] = useState('No title');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [newSessionDuration, setNewSessionDuration] = useState('60');
  const [newSessionTutorIMId, setNewSessionTutorIMId] = useState('');
  const [recordSession, setRecordSession] = useState(false);
  
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showHourDropdown, setShowHourDropdown] = useState(false);
  const [showMinuteDropdown, setShowMinuteDropdown] = useState(false);

  const [courseTeachers, setCourseTeachers] = useState([]);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [courseStudents, setCourseStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [currentUserInstitutionMemberId, setCurrentUserInstitutionMemberId] = useState(null);

  const [isAddingSession, setIsAddingSession] = useState(false);
  const [addSessionError, setAddSessionError] = useState('');
  const [enrollStudentError, setEnrollStudentError] = useState('');
  const [teacherError, setTeacherError] = useState('');

  const [studentTabView, setStudentTabView] = useState('current');
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [teacherSearchQuery, setTeacherSearchQuery] = useState('');
  const [teacherTabView, setTeacherTabView] = useState('current');

  useEffect(() => {
    stateRef.current = {
      sessions,
      courseTeachers,
      user,
      selectedDate,
      selectedHour,
      selectedMinute,
      newSessionTitle,
      newSessionDuration,
      newSessionTutorIMId,
      recordSession,
      courseId,
      currentUserInstitutionMemberId,
      recordingConsentSession
    };
  });

  const getNextAvailableTime = useCallback(() => {
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil((minutes + 1) / 5) * 5;
    
    if (roundedMinutes >= 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(roundedMinutes - 60);
    } else {
      now.setMinutes(roundedMinutes);
    }
    
    now.setSeconds(0);
    now.setMilliseconds(0);
    
    if (now.getHours() === 23 && now.getMinutes() > 30) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow;
    }
    
    return now;
  }, []);

  useEffect(() => {
    const pageTitle = courseName 
      ? `${institutionName || t('sessions.defaultInstitution')} - ${courseName}` 
      : `${institutionName || t('sessions.defaultInstitution')} - ${t('navigation.sessions')}`;
    document.title = pageTitle;
  }, [institutionName, courseName, t]);

  useEffect(() => {
    if (!isConnected || !courseId) return;
    
    wsSendMessage({
      type: 'subscribe_session_list',
      courseId: parseInt(courseId)
    });
    
    wsSendMessage({
      type: 'subscribe_institution'
    });
    
    const unsubscribeSessionUpdate = addMessageHandler('session_update', (data) => {
      console.log('SessionList received session_update:', data);
      
      if (data.action === 'created' && data.session) {
        const sessionCourseId = data.session.course_id || data.session.courseId || data.courseId;
        if (sessionCourseId && String(sessionCourseId) !== String(courseId)) {
          console.log('Session for different course, ignoring');
          return;
        }
        
        const newSession = {
          ...data.session,
          start_time: data.session.start_time || data.session.startTime,
          end_time: data.session.end_time || data.session.endTime,
          session_status: data.session.session_status || data.session.status || 'scheduled'
        };
        
        if (!newSession.start_time || !newSession.end_time) {
          console.warn('Session missing time fields:', newSession);
          return;
        }
        
        if (newSession.session_status) {
          setSessionStatuses(prev => ({
            ...prev,
            [newSession.id]: newSession.session_status
          }));
        }
        
        const now = new Date();
        const endTime = safeParseDate(newSession.end_time);
        if (!endTime) return;
        
        const isUpcoming = endTime >= now && newSession.session_status !== 'ended';
        
        setSessions(prev => {
          const existsInUpcoming = prev.upcoming.some(s => s.id === newSession.id);
          const existsInCompleted = prev.completed.some(s => s.id === newSession.id);
          
          if (existsInUpcoming || existsInCompleted) {
            return prev;
          }
          
          if (isUpcoming) {
            return {
              ...prev,
              upcoming: [...prev.upcoming, newSession].sort((a, b) => {
                const aDate = safeParseDate(a.start_time || a.startTime);
                const bDate = safeParseDate(b.start_time || b.startTime);
                if (!aDate) return 1;
                if (!bDate) return -1;
                return aDate - bDate;
              })
            };
          } else {
            return {
              ...prev,
              completed: [...prev.completed, newSession].sort((a, b) => {
                const aDate = safeParseDate(a.end_time || a.endTime);
                const bDate = safeParseDate(b.end_time || b.endTime);
                if (!aDate) return 1;
                if (!bDate) return -1;
                return bDate - aDate;
              })
            };
          }
        });
        
        toast.success(tRef.current('sessions.toast.newSession', { 
          title: newSession.title || tRef.current('sessions.untitled')
        }), {
          duration: 5000
        });
        
      } else if (data.action === 'updated' && data.session) {
        if (data.session.session_status || data.session.status) {
          setSessionStatuses(prev => ({
            ...prev,
            [data.session.id]: data.session.session_status || data.session.status
          }));
        }
        
        setSessions(prev => {
          const updateSession = (list) => 
            list.map(s => s.id === data.session.id ? {
              ...s,
              ...data.session,
              start_time: data.session.start_time || data.session.startTime || s.start_time,
              end_time: data.session.end_time || data.session.endTime || s.end_time,
              session_status: data.session.session_status || data.session.status || s.session_status
            } : s);
          
          return {
            upcoming: updateSession(prev.upcoming),
            completed: updateSession(prev.completed)
          };
        });
        
        toast.info(tRef.current('sessions.toast.sessionUpdated', { 
          title: data.session.title || tRef.current('sessions.untitled')
        }));
        
      } else if (data.action === 'deleted' && (data.sessionId || data.session?.id)) {
        const sessionId = data.sessionId || data.session?.id;
        
        setSessionStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[sessionId];
          return newStatuses;
        });
        
        setSessionDataMap(prev => {
          const newDataMap = { ...prev };
          delete newDataMap[sessionId];
          return newDataMap;
        });
        
        setSessions(prev => ({
          upcoming: prev.upcoming.filter(s => s.id !== sessionId),
          completed: prev.completed.filter(s => s.id !== sessionId)
        }));
        
        toast.info(tRef.current('sessions.toast.sessionDeleted'));
      }
    });
    
    const unsubscribeStatusChange = addMessageHandler('session_status_change', (data) => {
      const { sessionId, status, sessionData } = data;
      const normalizedId = parseInt(sessionId, 10);
      
      console.log('SessionList received session_status_change:', { sessionId: normalizedId, status, hasSessionData: !!sessionData });
      console.log('🔍 RAW WebSocket data:', data);
      console.log('🔍 attendance_details:', sessionData?.attendance_details);
      console.log('🔍 chat_log_available:', sessionData?.chat_log_available);
      
      // Always update the status
      setSessionStatuses(prev => ({
        ...prev,
        [normalizedId]: status
      }));
      
      // Update the session in the lists
      setSessions(prev => {
        let newUpcoming = [...prev.upcoming];
        let newCompleted = [...prev.completed];
        
        // Find and update in upcoming
        const upcomingIndex = newUpcoming.findIndex(s => s.id === normalizedId);
        if (upcomingIndex !== -1) {
          // Update the session with new data if provided
          if (sessionData) {
            newUpcoming[upcomingIndex] = {
              ...newUpcoming[upcomingIndex],
              ...sessionData,
              session_status: status
            };
          } else {
            newUpcoming[upcomingIndex] = {
              ...newUpcoming[upcomingIndex],
              session_status: status,
              started: status === 'live',
              ended: status === 'ended'  // ADD THIS
            };
          }
          
          // Move to completed if ended
          if (status === 'ended' || status === 'completed' || status === 'cancelled') {
            const [movedSession] = newUpcoming.splice(upcomingIndex, 1);
            movedSession.session_status = status;
            movedSession.started = false;
            
            newCompleted = [movedSession, ...newCompleted].sort((a, b) => {
              const aDate = safeParseDate(a.end_time || a.endTime);
              const bDate = safeParseDate(b.end_time || b.endTime);
              if (!aDate) return 1;
              if (!bDate) return -1;
              return bDate - aDate;
            });
          }
        }
        
        // Find and update in completed
        const completedIndex = newCompleted.findIndex(s => s.id === normalizedId);
        if (completedIndex !== -1) {
          if (sessionData) {
            newCompleted[completedIndex] = {
              ...newCompleted[completedIndex],
              ...sessionData,
              session_status: status
            };
          } else {
            newCompleted[completedIndex] = {
              ...newCompleted[completedIndex],
              session_status: status,
              started: status === 'live',
              ended: status === 'ended'
            };
          }
        }

         // If session not found but we have sessionData, check if it belongs to this course
        if (upcomingIndex === -1 && completedIndex === -1 && sessionData) {
          const sessionCourseId = sessionData.course_id || sessionData.courseId;
          
          if (sessionCourseId && String(sessionCourseId) === String(courseId)) {
            const normalizedSession = {
              ...sessionData,
              start_time: sessionData.start_time || sessionData.startTime,
              end_time: sessionData.end_time || sessionData.endTime,
              session_status: status
            };
            
            const now = new Date();
            const endTime = safeParseDate(normalizedSession.end_time);
            
            if (endTime && endTime >= now && status !== 'ended') {
              // Add to upcoming
              newUpcoming = [...newUpcoming, normalizedSession].sort((a, b) => {
                const aDate = safeParseDate(a.start_time || a.startTime);
                const bDate = safeParseDate(b.start_time || b.startTime);
                if (!aDate) return 1;
                if (!bDate) return -1;
                return aDate - bDate;
              });
            } else if (endTime) {
              // Add to completed
              newCompleted = [...newCompleted, normalizedSession].sort((a, b) => {
                const aDate = safeParseDate(a.end_time || a.endTime);
                const bDate = safeParseDate(b.end_time || b.endTime);
                if (!aDate) return 1;
                if (!bDate) return -1;
                return bDate - aDate;
              });
            }
          }
        }
        
        return {
          upcoming: newUpcoming,
          completed: newCompleted
        };
      });
      
      // Update session data map if ended - REFETCH from API for complete data
      if (status === 'ended') {
          console.log('🔍 Session ended, refetching complete data from API');
          
          // Refetch the session list to get complete attendance data
          loadSessions();
      }
      
      // Show notifications
      if (status === 'live') {
        const currentSessions = sessionsRef.current;
        const session = [...currentSessions.upcoming, ...currentSessions.completed].find(s => s.id === normalizedId);
        if (session) {
          toast.success(tRef.current('sessions.toast.sessionLive', { 
            title: session.title || tRef.current('sessions.session')
          }), {
            duration: 10000,
            action: {
              label: tRef.current('sessions.toast.joinNow'),
              onClick: () => navigate(`/sessions/${normalizedId}`)
            }
          });
        }
      } else if (status === 'ended') {
        toast.info(tRef.current('sessions.toast.sessionEnded'), {
          duration: 4000
        });
      }
    });

    const unsubscribeCalendarUpdate = addMessageHandler('calendar_update', (data) => {
      console.log('SessionList received calendar_update:', data);
      
      if (data.action === 'created' && data.session && data.courseId) {
        // Only add if it's for the current course
        if (String(data.courseId) === String(courseId)) {
          const newSession = {
            ...data.session,
            start_time: data.session.start_time || data.session.startTime,
            end_time: data.session.end_time || data.session.endTime,
            session_status: data.session.session_status || data.session.status || 'scheduled'
          };
          
          // Determine if it's upcoming or completed
          const now = new Date();
          const endTime = safeParseDate(newSession.end_time);
          
          if (!endTime) return;
          
          const isUpcoming = endTime >= now && newSession.session_status !== 'ended';
          
          setSessions(prev => {
            // Check if session already exists
            const existsInUpcoming = prev.upcoming.some(s => s.id === newSession.id);
            const existsInCompleted = prev.completed.some(s => s.id === newSession.id);
            
            if (existsInUpcoming || existsInCompleted) {
              return prev;
            }
            
            if (isUpcoming) {
              // Add to upcoming sessions and sort
              const newUpcoming = [...prev.upcoming, newSession].sort((a, b) => {
                const aDate = safeParseDate(a.start_time || a.startTime);
                const bDate = safeParseDate(b.start_time || b.startTime);
                if (!aDate) return 1;
                if (!bDate) return -1;
                return aDate - bDate;
              });
              
              return {
                ...prev,
                upcoming: newUpcoming
              };
            } else {
              // Add to completed sessions and sort
              const newCompleted = [...prev.completed, newSession].sort((a, b) => {
                const aDate = safeParseDate(a.end_time || a.endTime);
                const bDate = safeParseDate(b.end_time || b.endTime);
                if (!aDate) return 1;
                if (!bDate) return -1;
                return bDate - aDate;
              });
              
              return {
                ...prev,
                completed: newCompleted
              };
            }
          });
          
          // Show notification
          toast.success(tRef.current('sessions.toast.newSession', { 
            title: newSession.title || tRef.current('sessions.untitled')
          }), {
            duration: 5000
          });
        }
      } else if (data.action === 'deleted' && data.session && data.courseId) {
        // Only remove if it's for the current course
        if (String(data.courseId) === String(courseId)) {
          const sessionId = data.session.id;
          
          setSessions(prev => {
            // Remove from both upcoming and completed
            const newUpcoming = prev.upcoming.filter(s => s.id !== sessionId);
            const newCompleted = prev.completed.filter(s => s.id !== sessionId);
            
            // Only update if something changed
            if (newUpcoming.length !== prev.upcoming.length || 
                newCompleted.length !== prev.completed.length) {
              return {
                upcoming: newUpcoming,
                completed: newCompleted
              };
            }
            
            return prev;
          });
          
          // Remove session status and data
          setSessionStatuses(prev => {
            const updated = { ...prev };
            delete updated[sessionId];
            return updated;
          });
          
          setSessionDataMap(prev => {
            const updated = { ...prev };
            delete updated[sessionId];
            return updated;
          });
          
          // Show notification
          toast.info(tRef.current('sessions.toast.sessionDeleted'), {
            duration: 3000
          });
        }
      }
    });

    return () => {
      wsSendMessage({
        type: 'unsubscribe_session_list',
        courseId: parseInt(courseId)
      });
      wsSendMessage({
        type: 'unsubscribe_institution'
      });
      unsubscribeSessionUpdate();
      unsubscribeStatusChange();
      unsubscribeCalendarUpdate();
    };
  }, [isConnected, courseId, wsSendMessage, addMessageHandler, navigate]);

  useEffect(() => {
    const allSessions = [...sessions.upcoming, ...sessions.completed];
    const initialStatuses = {};
    
    allSessions.forEach(session => {
      if (session.session_status) {
        initialStatuses[session.id] = session.session_status;
      } else if (session.started === true) {
        initialStatuses[session.id] = 'live';
      }
    });
    
    setSessionStatuses(prev => ({ ...prev, ...initialStatuses }));
  }, [sessions]);

  const getDaysInMonth = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startingDayOfWeek = firstDay.getDay();
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  }, []);

  const isToday = useCallback((date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }, []);

  const isPastDate = useCallback((date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }, []);

  const getAvailableHours = useCallback(() => {
    const hours = [];
    const now = new Date();
    const isSelectedToday = selectedDate?.toDateString() === now.toDateString();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    for (let i = 0; i < 24; i++) {
      if (!isSelectedToday || i > currentHour || (i === currentHour && currentMinute < 55)) {
        hours.push(i);
      }
    }
    return hours;
  }, [selectedDate]);

  const getAvailableMinutes = useCallback(() => {
    const minutes = [];
    const now = new Date();
    const isSelectedToday = selectedDate?.toDateString() === now.toDateString();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
   
    for (let i = 0; i < 60; i += 5) {
      if (!isSelectedToday || 
          selectedHour > currentHour || 
          (selectedHour === currentHour && i > currentMinute)) {
        minutes.push(i);
      }
    }
    return minutes;
  }, [selectedDate, selectedHour]);

  const scrollToSelected = useCallback((dropdownRef, value, itemHeight = 28) => {
    setTimeout(() => {
      if (dropdownRef.current) {
        const index = dropdownRef.current.querySelector(`[data-value="${value}"]`)?.dataset.index;
        if (index) {
          dropdownRef.current.scrollTop = parseInt(index) * itemHeight - 56;
        }
      }
    }, 10);
  }, []);

  const loadStudentsData = useCallback(async () => {
    if (!courseId) return;
    
    try {
      const [enrolledResponse, availableResponse] = await Promise.all([
        getCourseStudents(courseId),
        getAvailableStudentsForCourse(courseId)
      ]);

      if (enrolledResponse.success) {
        setCourseStudents(enrolledResponse.data || []);
      }
      if (availableResponse.success) {
        setAvailableStudents(availableResponse.data || []);
      }
    } catch (err) {
      console.error('Failed to load students data:', err);
    }
  }, [courseId]);

  const loadAvailableTeachersData = useCallback(async () => {
    if (!courseId) return;
    
    try {
      const response = await getAvailableTeachersForCourse(courseId);
      if (response.success) {
        setAvailableTeachers(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load available teachers:', err);
    }
  }, [courseId]);

  const loadCourseTeachers = useCallback(async () => {
    if (!courseId || userRole !== 'teacher') return;

    try {
      const response = await getCourseTeachers(courseId);
      if (response.success && Array.isArray(response.data)) {
        setCourseTeachers(response.data);
        
        const currentTeacher = response.data.find(
          t => t.user_id === user?.id || t.user_id === user?.userId
        );
        if (currentTeacher) {
          setIsOwner(currentTeacher.role === 'owner'); 
          setCurrentUserInstitutionMemberId(currentTeacher.institution_member_id);
        }
      }
    } catch (err) {
      console.error('Failed to load course teachers:', err);
    }
  }, [courseId, userRole, user]);

  const loadCourseDetails = useCallback(async () => {
    if (!courseId || courseName) return;
    
    try {
      const response = await getCourse(courseId);
      if (response.success && response.data) {
        setCourseName(response.data.name);
        if (onCourseNameLoad) {
          onCourseNameLoad(response.data.name);
        }
      }
    } catch (err) {
      console.error('Failed to load course details:', err);
    }
  }, [courseId, courseName, onCourseNameLoad]);

  const loadSessions = useCallback(async () => {
    if (!courseId) return;

    setIsLoading(true);
    setError('');
    try {
      const response = await getCourseSessions(courseId);
      if (response.success && Array.isArray(response.data)) {
        const now = new Date();
        
        const initialStatuses = {};
        response.data.forEach(session => {
          if (session.session_status) {
            initialStatuses[session.id] = session.session_status;
          } else if (session.started === true) {
            initialStatuses[session.id] = 'live';
          }
        });
        setSessionStatuses(initialStatuses);
        
        const completedSessions = response.data
          .filter(session => {
            const endTime = safeParseDate(session.end_time || session.endTime);
            if (!endTime) return false;
            return session.session_status === 'ended' || endTime < now;
          })
          .sort((a, b) => {
            const aDate = safeParseDate(a.end_time || a.endTime);
            const bDate = safeParseDate(b.end_time || b.endTime);
            if (!aDate) return 1;
            if (!bDate) return -1;
            return bDate - aDate;
          });
          
        const upcomingSessions = response.data
          .filter(session => {
            const endTime = safeParseDate(session.end_time || session.endTime);
            if (!endTime) return false;
            return session.session_status !== 'ended' && endTime >= now;
          })
          .sort((a, b) => {
            const aDate = safeParseDate(a.start_time || a.startTime);
            const bDate = safeParseDate(b.start_time || b.startTime);
            if (!aDate) return 1;
            if (!bDate) return -1;
            return aDate - bDate;
          });
          
        setSessions({ completed: completedSessions, upcoming: upcomingSessions });
        
        const firstSession = response.data[0];
        if (firstSession?.course?.name) {
          setCourseName(firstSession.course.name);
          if (onCourseNameLoad) {
            onCourseNameLoad(firstSession.course.name);
          }
        }
      } else {
        setError(response.message || t('sessions.errors.loadFailed'));
      }
    } catch (err) {
      setError(err.message || t('sessions.errors.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [courseId, onCourseNameLoad, t]);

  useEffect(() => {
    loadCourseDetails();
    loadSessions();
    loadCourseTeachers();
  }, [loadCourseDetails, loadSessions, loadCourseTeachers]);

  useMemo(() => {
    callbacksRef.current = {
      handleJoinSession: (session) => {
        if (session && session.recording_enabled) {
          setRecordingConsentSession(session);
        } else {
          navigate(`/sessions/${session.id}`);
        }
      },
      
      handleDeleteSession: async (sessionId) => {
        if (!window.confirm(tRef.current('sessions.confirmDelete'))) {
          return;
        }
        try {
          const response = await deleteSession(sessionId);
          if (response.success) {
            toast.success(tRef.current('sessions.toast.deleteSuccess'));
            const state = stateRef.current;
            if (state.courseId) {
              const sessionsResponse = await getCourseSessions(state.courseId);
              if (sessionsResponse.success && Array.isArray(sessionsResponse.data)) {
                const now = new Date();
                
                const completedSessions = sessionsResponse.data
                  .filter(session => {
                    const endTime = safeParseDate(session.end_time || session.endTime);
                    if (!endTime) return false;
                    return session.session_status === 'ended' || endTime < now;
                  })
                  .sort((a, b) => {
                    const aDate = safeParseDate(a.end_time || a.endTime);
                    const bDate = safeParseDate(b.end_time || b.endTime);
                    if (!aDate) return 1;
                    if (!bDate) return -1;
                    return bDate - aDate;
                  });
                  
                const upcomingSessions = sessionsResponse.data
                  .filter(session => {
                    const endTime = safeParseDate(session.end_time || session.endTime);
                    if (!endTime) return false;
                    return session.session_status !== 'ended' && endTime >= now;
                  })
                  .sort((a, b) => {
                    const aDate = safeParseDate(a.start_time || a.startTime);
                    const bDate = safeParseDate(b.start_time || b.startTime);
                    if (!aDate) return 1;
                    if (!bDate) return -1;
                    return aDate - bDate;
                  });
                  
                setSessions({ completed: completedSessions, upcoming: upcomingSessions });
              }
            }
          } else {
            toast.error(response.message || tRef.current('sessions.toast.deleteFailed'));
          }
        } catch (err) {
          toast.error(err.message || tRef.current('sessions.toast.deleteFailed'));
        }
      },
      
      openChatModal: (session) => {
        setModalStates(prev => ({ 
          ...prev, 
          showChatModal: true,
          selectedSessionForChat: session
        }));
      },
      
      handleAddSession: async () => {
        const state = stateRef.current;
        setIsAddingSession(true);
        setAddSessionError('');

        try {
          if (!state.selectedDate || state.selectedHour === null || state.selectedMinute === null) {
            setAddSessionError(tRef.current('sessions.errors.selectDateTime'));
            setIsAddingSession(false);
            return;
          }

          const d = state.selectedDate;
          const sessionDateTime = new Date(
            d.getFullYear(),
            d.getMonth(),
            d.getDate(),
            state.selectedHour,
            state.selectedMinute,
            0, 0
          );
          
          const now = new Date();
          if (sessionDateTime <= now) {
            setAddSessionError(tRef.current('sessions.errors.pastDateTime'));
            setIsAddingSession(false);
            return;
          }

          const endDateTime = new Date(sessionDateTime.getTime() + parseInt(state.newSessionDuration) * 60000);

          let tutorId = state.newSessionTutorIMId;
          
          if (!tutorId && state.courseTeachers.length === 1) {
            const currentTeacher = state.courseTeachers.find(
              t => t.user_id === state.user?.id || t.user_id === state.user?.userId
            );
            if (currentTeacher) {
              tutorId = currentTeacher.institution_member_id;
            }
          }
          
          if (!tutorId) {
            tutorId = state.currentUserInstitutionMemberId || state.user?.institutionMemberId;
          }
          
          if (!tutorId) {
            setAddSessionError(tRef.current('sessions.errors.noTeacher'));
            setIsAddingSession(false);
            return;
          }

          const sessionData = {
            courseId: parseInt(state.courseId),
            title: state.newSessionTitle.trim() || tRef.current('sessions.noTitle'),
            startTime: sessionDateTime.toISOString(),
            endTime: endDateTime.toISOString(),
            tutorInstitutionMemberId: parseInt(tutorId),
            recordingEnabled: state.recordSession
          };

          console.log('Creating session with data:', sessionData);

          const response = await createSession(null, sessionData);
          if (response.success) {
            callbacksRef.current.closeAddSessionModal();
            toast.success(tRef.current('sessions.toast.createSuccess'));
            if (state.courseId) {
              const sessionsResponse = await getCourseSessions(state.courseId);
              if (sessionsResponse.success && Array.isArray(sessionsResponse.data)) {
                const now = new Date();
                
                const completedSessions = sessionsResponse.data
                  .filter(session => {
                    const endTime = safeParseDate(session.end_time || session.endTime);
                    if (!endTime) return false;
                    return session.session_status === 'ended' || endTime < now;
                  })
                  .sort((a, b) => {
                    const aDate = safeParseDate(a.end_time || a.endTime);
                    const bDate = safeParseDate(b.end_time || b.endTime);
                    if (!aDate) return 1;
                    if (!bDate) return -1;
                    return bDate - aDate;
                  });
                  
                const upcomingSessions = sessionsResponse.data
                  .filter(session => {
                    const endTime = safeParseDate(session.end_time || session.endTime);
                    if (!endTime) return false;
                    return session.session_status !== 'ended' && endTime >= now;
                  })
                  .sort((a, b) => {
                    const aDate = safeParseDate(a.start_time || a.startTime);
                    const bDate = safeParseDate(b.start_time || b.startTime);
                    if (!aDate) return 1;
                    if (!bDate) return -1;
                    return aDate - bDate;
                  });
                  
                setSessions({ completed: completedSessions, upcoming: upcomingSessions });
              }
            }
          } else {
            setAddSessionError(response.message || tRef.current('sessions.toast.createFailed'));
          }
        } catch (err) {
          setAddSessionError(err.message || tRef.current('sessions.toast.createFailed'));
        } finally {
          setIsAddingSession(false);
        }
      },
      
      openAddSessionModal: () => {
        const state = stateRef.current;
        const nextTime = getNextAvailableTime();
        setNewSessionTitle(tRef.current('sessions.noTitle'));
        setSelectedDate(nextTime);
        setSelectedHour(nextTime.getHours());
        setSelectedMinute(Math.round(nextTime.getMinutes() / 5) * 5);
        setNewSessionDuration('60');
        setRecordSession(false);
        setCalendarMonth(new Date());
        
        if (state.courseTeachers.length > 0) {
          const currentTeacher = state.courseTeachers.find(
            t => t.user_id === state.user?.id || t.user_id === state.user?.userId
          );
          if (currentTeacher) {
            setNewSessionTutorIMId(currentTeacher.institution_member_id.toString());
          } else if (state.courseTeachers.length === 1) {
            setNewSessionTutorIMId(state.courseTeachers[0].institution_member_id.toString());
          }
        } else if (state.currentUserInstitutionMemberId) {
          setNewSessionTutorIMId(state.currentUserInstitutionMemberId.toString());
        } else if (state.user?.institutionMemberId) {
          setNewSessionTutorIMId(state.user.institutionMemberId.toString());
        }
        
        setModalStates(prev => ({ ...prev, showAddSessionModal: true }));
      },
      
      closeAddSessionModal: () => {
        setModalStates(prev => ({ ...prev, showAddSessionModal: false }));
      },
      
      openStudentsModal: () => {
        setModalStates(prev => ({ ...prev, showStudentsModal: true }));
        loadStudentsData();
      },
      
      closeStudentsModal: () => {
        setModalStates(prev => ({ ...prev, showStudentsModal: false }));
        setStudentSearchQuery('');
        setStudentTabView('current');
      },
      
      openTeachersModal: () => {
        if (isOwner) {
          setModalStates(prev => ({ ...prev, showTeachersModal: true }));
          loadAvailableTeachersData();
        }
      },
      
      closeTeachersModal: () => {
        setModalStates(prev => ({ ...prev, showTeachersModal: false }));
        setTeacherSearchQuery('');
        setTeacherTabView('current');
      },
      
      closeChatModal: () => {
        setModalStates(prev => ({ 
          ...prev, 
          showChatModal: false,
          selectedSessionForChat: null
        }));
      },
      
      handleTabChange: (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        navigate(`/courses/${courseId}/${tab}`, { replace: true });
      },
      
      handleBackToCourses: () => {
        navigate('/courses');
      },
      
      handlePageChange: (page) => {
        setCurrentPage(page);
      },
      
      handleRecordingConsent: () => {
        const state = stateRef.current;
        if (state.recordingConsentSession) {
          navigate(`/sessions/${state.recordingConsentSession.id}`);
          setRecordingConsentSession(null);
        }
      },
      
      handleRecordingCancel: () => {
        setRecordingConsentSession(null);
      },
      
      handleEnrollStudent: async (institutionMemberId) => {
        setEnrollStudentError('');
        try {
          const response = await enrollStudentsInCourse(courseId, institutionMemberId);
          if (response.success) {
            toast.success(tRef.current('sessions.toast.studentEnrolled'));
            loadStudentsData();
          } else {
            setEnrollStudentError(response.message || tRef.current('sessions.toast.enrollFailed'));
          }
        } catch (err) {
          setEnrollStudentError(err.message || tRef.current('sessions.toast.enrollFailed'));
        }
      },
      
      handleUnenrollStudent: async (institutionMemberId) => {
        if (!window.confirm(tRef.current('sessions.confirmUnenroll'))) {
          return;
        }
        try {
          const response = await unenrollStudentsFromCourse(courseId, institutionMemberId);
          if (response.success) {
            toast.success(tRef.current('sessions.toast.studentRemoved'));
            loadStudentsData();
          } else {
            toast.error(response.message || tRef.current('sessions.toast.removeFailed'));
          }
        } catch (err) {
          toast.error(err.message || tRef.current('sessions.toast.removeFailed'));
        }
      },
      
      handleAddTeacher: async (institutionMemberId) => {
        setTeacherError('');
        try {
          const response = await addCourseTeacher(courseId, institutionMemberId);
          if (response.success) {
            toast.success(tRef.current('sessions.toast.teacherAdded'));
            loadCourseTeachers();
            loadAvailableTeachersData();
          } else {
            setTeacherError(response.message || tRef.current('sessions.toast.addTeacherFailed'));
          }
        } catch (err) {
          setTeacherError(err.message || tRef.current('sessions.toast.addTeacherFailed'));
        }
      },
      
      handleRemoveTeacher: async (institutionMemberId) => {
        if (!window.confirm(tRef.current('sessions.confirmRemoveTeacher'))) {
          return;
        }
        try {
          const response = await removeCourseTeacher(courseId, institutionMemberId);
          if (response.success) {
            toast.success(tRef.current('sessions.toast.teacherRemoved'));
            loadCourseTeachers();
            loadAvailableTeachersData();
          } else {
            toast.error(response.message || tRef.current('sessions.toast.removeTeacherFailed'));
          }
        } catch (err) {
          toast.error(err.message || tRef.current('sessions.toast.removeTeacherFailed'));
        }
      }
    };
  }, [courseId, navigate, getCourseSessions, loadStudentsData, loadCourseTeachers, loadAvailableTeachersData, getNextAvailableTime, isOwner]);

  const handleJoinSession = useCallback((session) => {
    callbacksRef.current.handleJoinSession(session);
  }, []);

  const handleDeleteSession = useCallback((sessionId) => {
    callbacksRef.current.handleDeleteSession(sessionId);
  }, []);

  const openChatModal = useCallback((session) => {
    callbacksRef.current.openChatModal(session);
  }, []);

  const handleAddSession = useCallback(() => {
    callbacksRef.current.handleAddSession();
  }, []);

  const openAddSessionModal = useCallback(() => {
    callbacksRef.current.openAddSessionModal();
  }, []);

  const closeAddSessionModal = useCallback(() => {
    callbacksRef.current.closeAddSessionModal();
  }, []);

  const openStudentsModal = useCallback(() => {
    callbacksRef.current.openStudentsModal();
  }, []);

  const closeStudentsModal = useCallback(() => {
    callbacksRef.current.closeStudentsModal();
  }, []);

  const openTeachersModal = useCallback(() => {
    callbacksRef.current.openTeachersModal();
  }, []);

  const closeTeachersModal = useCallback(() => {
    callbacksRef.current.closeTeachersModal();
  }, []);

  const closeChatModal = useCallback(() => {
    callbacksRef.current.closeChatModal();
  }, []);

  const handleTabChange = useCallback((tab) => {
    callbacksRef.current.handleTabChange(tab);
  }, []);

  const handleBackToCourses = useCallback(() => {
    callbacksRef.current.handleBackToCourses();
  }, []);

  const handlePageChange = useCallback((page) => {
    callbacksRef.current.handlePageChange(page);
  }, []);

  const handleRecordingConsent = useCallback(() => {
    callbacksRef.current.handleRecordingConsent();
  }, []);

  const handleRecordingCancel = useCallback(() => {
    callbacksRef.current.handleRecordingCancel();
  }, []);

  const handleEnrollStudent = useCallback((institutionMemberId) => {
    callbacksRef.current.handleEnrollStudent(institutionMemberId);
  }, []);

  const handleUnenrollStudent = useCallback((institutionMemberId) => {
    callbacksRef.current.handleUnenrollStudent(institutionMemberId);
  }, []);

  const handleAddTeacher = useCallback((institutionMemberId) => {
    callbacksRef.current.handleAddTeacher(institutionMemberId);
  }, []);

  const handleRemoveTeacher = useCallback((institutionMemberId) => {
    callbacksRef.current.handleRemoveTeacher(institutionMemberId);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown-container')) {
        setShowHourDropdown(false);
        setShowMinuteDropdown(false);
      }
    };
    
    if (modalStates.showAddSessionModal) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [modalStates.showAddSessionModal]);

  useEffect(() => {
    if (showHourDropdown && selectedHour !== null) {
      scrollToSelected(hourDropdownRef, selectedHour);
    }
  }, [showHourDropdown, selectedHour, scrollToSelected]);

  useEffect(() => {
    if (showMinuteDropdown && selectedMinute !== null) {
      scrollToSelected(minuteDropdownRef, selectedMinute);
    }
  }, [showMinuteDropdown, selectedMinute, scrollToSelected]);

  const filteredCourseStudents = useMemo(() => 
    courseStudents.filter(student => {
      const searchLower = studentSearchQuery.toLowerCase();
      return (student.display_name || '').toLowerCase().includes(searchLower) ||
             (student.email || '').toLowerCase().includes(searchLower);
    }), [courseStudents, studentSearchQuery]
  );

  const filteredAvailableStudents = useMemo(() => 
    availableStudents.filter(student => {
      const searchLower = studentSearchQuery.toLowerCase();
      return (student.display_name || '').toLowerCase().includes(searchLower) ||
             (student.email || '').toLowerCase().includes(searchLower);
    }), [availableStudents, studentSearchQuery]
  );

  const filteredCourseTeachers = useMemo(() => 
    courseTeachers.filter(teacher => {
      const searchLower = teacherSearchQuery.toLowerCase();
      return (teacher.display_name || '').toLowerCase().includes(searchLower) ||
             (teacher.email || '').toLowerCase().includes(searchLower);
    }), [courseTeachers, teacherSearchQuery]
  );

  const filteredAvailableTeachers = useMemo(() => 
    availableTeachers.filter(teacher => {
      const searchLower = teacherSearchQuery.toLowerCase();
      return (teacher.display_name || '').toLowerCase().includes(searchLower) ||
             (teacher.email || '').toLowerCase().includes(searchLower);
    }), [availableTeachers, teacherSearchQuery]
  );

  const durationOptions = useMemo(() => [
    { value: '30', label: t('sessions.duration.30min') },
    { value: '45', label: t('sessions.duration.45min') },
    { value: '60', label: t('sessions.duration.1hour') },
    { value: '90', label: t('sessions.duration.1point5hours') },
    { value: '120', label: t('sessions.duration.2hours') },
    { value: '180', label: t('sessions.duration.3hours') },
  ], [t]);

  return (
    <>
      <div className="w-full h-full overflow-y-auto">
        <div className="w-full mx-auto px-4 sm:px-6 py-4 sm:py-8 sm:max-w-4xl">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div className="mb-4 sm:mb-0">
              <button
                onClick={handleBackToCourses}
                className="flex items-center text-cyan-600 hover:text-cyan-700 mb-2 text-sm sm:text-base"
              >
                <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                {t('sessions.backToCourses')}
              </button>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-800">
                {courseName || (
                  <div className="h-8 w-48 bg-slate-200 rounded animate-pulse"></div>
                )}
              </h2>
            </div>

            {userRole === 'teacher' && (
              <div className="flex gap-2">
                <button
                  onClick={openStudentsModal}
                  className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center"
                >
                  <UserGroupIcon className="w-5 h-5 mr-2" />
                  {t('sessions.buttons.students')}
                </button>
                <button
                  onClick={openTeachersModal}
                  disabled={!isOwner}
                  className={`px-4 py-2 border rounded-lg transition-colors flex items-center ${
                    isOwner 
                      ? 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 cursor-pointer' 
                      : 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                  title={!isOwner ? t('sessions.tooltips.onlyOwner') : t('sessions.tooltips.manageTeachers')}
                >
                  <UserIcon className="w-5 h-5 mr-2" />
                  {t('sessions.buttons.teachers')}
                  {!isOwner && <span className="ml-1 text-xs">({t('sessions.ownerOnly')})</span>}
                </button>
                <button
                  onClick={openAddSessionModal}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors flex items-center"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  {t('sessions.buttons.addSession')}
                </button>
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className="flex space-x-1 border-b border-slate-200">
              <button
                className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'upcoming'
                    ? 'text-cyan-600 border-b-2 border-cyan-600 bg-cyan-50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
                onClick={() => handleTabChange('upcoming')}
              >
                {t('sessions.tabs.upcoming', { count: sessions.upcoming.length })}
              </button>
              <button
                className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'completed'
                    ? 'text-cyan-600 border-b-2 border-cyan-600 bg-cyan-50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
                onClick={() => handleTabChange('completed')}
              >
                {t('sessions.tabs.completed', { count: sessions.completed.length })}
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
              <p className="text-slate-600">{t('sessions.loadingSessions')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <p>{error}</p>
            </div>
          ) : (
            <SessionListContent
              sessions={sessions}
              activeTab={activeTab}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              userRole={userRole}
              isOwner={isOwner}
              sessionStatuses={sessionStatuses}
              sessionDataMap={sessionDataMap}
              onJoinSession={handleJoinSession}
              onDeleteSession={handleDeleteSession}
              onViewChat={openChatModal}
              onAddSession={openAddSessionModal}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      <AddSessionModal
        isOpen={modalStates.showAddSessionModal}
        onClose={closeAddSessionModal}
        addSessionError={addSessionError}
        newSessionTitle={newSessionTitle}
        setNewSessionTitle={setNewSessionTitle}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedHour={selectedHour}
        setSelectedHour={setSelectedHour}
        selectedMinute={selectedMinute}
        setSelectedMinute={setSelectedMinute}
        newSessionDuration={newSessionDuration}
        setNewSessionDuration={setNewSessionDuration}
        newSessionTutorIMId={newSessionTutorIMId}
        setNewSessionTutorIMId={setNewSessionTutorIMId}
        recordSession={recordSession}
        setRecordSession={setRecordSession}
        calendarMonth={calendarMonth}
        setCalendarMonth={setCalendarMonth}
        showHourDropdown={showHourDropdown}
        setShowHourDropdown={setShowHourDropdown}
        showMinuteDropdown={showMinuteDropdown}
        setShowMinuteDropdown={setShowMinuteDropdown}
        hourDropdownRef={hourDropdownRef}
        minuteDropdownRef={minuteDropdownRef}
        courseTeachers={courseTeachers}
        user={user}
        isAddingSession={isAddingSession}
        onAddSession={handleAddSession}
        getDaysInMonth={getDaysInMonth}
        isToday={isToday}
        isPastDate={isPastDate}
        getAvailableHours={getAvailableHours}
        getAvailableMinutes={getAvailableMinutes}
        durationOptions={durationOptions}
      />

      <ManageStudentsModal
        isOpen={modalStates.showStudentsModal}
        onClose={closeStudentsModal}
        enrollStudentError={enrollStudentError}
        studentSearchQuery={studentSearchQuery}
        setStudentSearchQuery={setStudentSearchQuery}
        studentTabView={studentTabView}
        setStudentTabView={setStudentTabView}
        filteredCourseStudents={filteredCourseStudents}
        filteredAvailableStudents={filteredAvailableStudents}
        handleEnrollStudent={handleEnrollStudent}
        handleUnenrollStudent={handleUnenrollStudent}
      />

      <ManageTeachersModal
        isOpen={modalStates.showTeachersModal}
        onClose={closeTeachersModal}
        isOwner={isOwner}
        teacherError={teacherError}
        teacherSearchQuery={teacherSearchQuery}
        setTeacherSearchQuery={setTeacherSearchQuery}
        teacherTabView={teacherTabView}
        setTeacherTabView={setTeacherTabView}
        filteredCourseTeachers={filteredCourseTeachers}
        filteredAvailableTeachers={filteredAvailableTeachers}
        currentUserInstitutionMemberId={currentUserInstitutionMemberId}
        handleAddTeacher={handleAddTeacher}
        handleRemoveTeacher={handleRemoveTeacher}
      />

      <SessionChatModal
        isOpen={modalStates.showChatModal}
        sessionId={modalStates.selectedSessionForChat?.id}
        sessionTitle={modalStates.selectedSessionForChat?.title}
        onClose={closeChatModal}
      />

      {recordingConsentSession && (
        <RecordingConsentModal
          session={recordingConsentSession}
          onConsent={handleRecordingConsent}
          onCancel={handleRecordingCancel}
        />
      )}
    </>
  );
};

export default React.memo(SessionList, (prevProps, nextProps) => {
  return (
    prevProps.user?.id === nextProps.user?.id &&
    prevProps.userRole === nextProps.userRole &&
    prevProps.initialTab === nextProps.initialTab &&
    prevProps.institutionName === nextProps.institutionName &&
    prevProps.onCourseNameLoad === nextProps.onCourseNameLoad
  );
});